import React, {useState} from "react";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {Button} from "@/components/ui/button";
import {Badge} from "@/components/ui/badge";
import {Separator} from "@/components/ui/separator";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group";

import {
    CreditCard,
    RotateCcw,
    Percent,
    AlertTriangle,
    CheckCircle2,
} from "lucide-react";

interface EarlyClosePopupProps {
    open: boolean;
    remaining: number;
    initialAmount: number;
    onClose: () => void;
    onConfirm: (option: string, percentage?: number) => void;
}

const EarlyClosePopup: React.FC<EarlyClosePopupProps> = ({
                                                             open,
                                                             remaining,
                                                             initialAmount,
                                                             onClose,
                                                             onConfirm,
                                                         }) => {
    const [selectedOption, setSelectedOption] = useState<string>("");
    const [percentage, setPercentage] = useState<number | string>("");

    const handleOptionChange = (value: string) => {
        setSelectedOption(value);
        if (value !== "closeWithInterest") {
            setPercentage("");
        }
    };

    const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPercentage(value === "" ? "" : Number(value));
    };

    const calculateAmountWithInterest = () => {
        if (percentage === "" || isNaN(Number(percentage))) return remaining;
        const interest = (remaining * Number(percentage)) / 100;
        return remaining + interest;
    };

    const handleConfirm = () => {
        onConfirm(
            selectedOption,
            selectedOption === "closeWithInterest" ? Number(percentage) : undefined
        );
    };

    const withInterestTotal = calculateAmountWithInterest();
    const hasPercentError =
        selectedOption === "closeWithInterest" &&
        (percentage === "" || isNaN(Number(percentage)) || Number(percentage) < 0);

    return (
        <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
            <DialogContent className="sm:max-w-[560px] p-0 overflow-hidden">
                {/* Header */}
                <div className="p-4 sm:p-5 bg-muted/30">
                    <DialogHeader className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1">
                                <DialogTitle className="flex items-center gap-2 text-base">
                                    <div
                                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border bg-background">
                                        <CheckCircle2 className="h-4 w-4 text-muted-foreground"/>
                                    </div>
                                    Закрытие кредита
                                </DialogTitle>
                                <DialogDescription className="text-sm">
                                    Выбери сценарий. Суммы посчитаются автоматически.
                                </DialogDescription>
                            </div>

                            {/*<Button*/}
                            {/*    type="button"*/}
                            {/*    variant="ghost"*/}
                            {/*    size="icon"*/}
                            {/*    onClick={onClose}*/}
                            {/*    className="h-9 w-9"*/}
                            {/*>*/}
                            {/*    <X className="h-4 w-4" />*/}
                            {/*</Button>*/}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="secondary" className="rounded-full">
                                Остаток: {Number(remaining).toFixed(2)} сом
                            </Badge>
                            <Badge variant="outline" className="rounded-full">
                                Выдано: {Number(initialAmount).toFixed(2)} сом
                            </Badge>

                            {hasPercentError ? (
                                <Badge variant="destructive" className="rounded-full">
                                    <AlertTriangle className="h-3.5 w-3.5 mr-1"/>
                                    Проверь процент
                                </Badge>
                            ) : null}
                        </div>
                    </DialogHeader>
                </div>

                <Separator/>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
                    <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Опция закрытия</Label>

                        <RadioGroup
                            value={selectedOption}
                            onValueChange={handleOptionChange}
                            className="grid gap-2"
                        >
                            {/* earlyClose */}
                            <label className="flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition hover:bg-muted/20">
                                <RadioGroupItem value="earlyClose" className="mt-1" />
                                <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                                    <CreditCard className="h-4 w-4 text-muted-foreground"/>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium">Закрыть досрочно</div>
                                    <div className="text-xs text-muted-foreground">
                                        Закрытие по остатку долга
                                    </div>
                                </div>
                                <Badge variant="secondary" className="rounded-full">
                                    {Number(remaining).toFixed(2)} сом
                                </Badge>
                            </label>

                            {/* return */}
                            <label className="flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition hover:bg-muted/20">
                                <RadioGroupItem value="return" className="mt-1" />
                                <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                                    <RotateCcw className="h-4 w-4 text-muted-foreground"/>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium">Возврат</div>
                                    <div className="text-xs text-muted-foreground">
                                        Возврат по полной сумме выдачи
                                    </div>
                                </div>
                                <Badge variant="secondary" className="rounded-full">
                                    {Number(initialAmount).toFixed(2)} сом
                                </Badge>
                            </label>

                            {/* closeWithInterest */}
                            <label className="flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition hover:bg-muted/20">
                                <RadioGroupItem value="closeWithInterest" className="mt-1" />
                                <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                                    <Percent className="h-4 w-4 text-muted-foreground"/>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium">Закрыть с процентом</div>
                                    <div className="text-xs text-muted-foreground">
                                        К остатку долга добавится процент
                                    </div>
                                </div>
                                <Badge variant="outline" className="rounded-full">
                                    {withInterestTotal.toFixed(2)} сом
                                </Badge>
                            </label>
                        </RadioGroup>
                    </div>

                    {selectedOption === "closeWithInterest" ? (
                        <div className="rounded-xl border bg-muted/20 p-3 space-y-2">
                                    <div className="flex items-center justify-between gap-3">
                                <div className="text-xs text-muted-foreground">
                                    Укажи процент к остатку
                                </div>
                                <Badge variant="secondary" className="rounded-full">
                                    Итог: {Number(withInterestTotal).toFixed(2)} сом
                                </Badge>
                            </div>

                            <div className="grid gap-2 sm:grid-cols-[1fr_180px] sm:items-end">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">
                                        Процент
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            inputMode="numeric"
                                            value={percentage}
                                            onChange={handlePercentageChange}
                                            className={[
                                                "pr-10",
                                                hasPercentError
                                                    ? "border-destructive focus-visible:ring-destructive"
                                                    : "",
                                            ].join(" ")}
                                            placeholder="Напр. 10"
                                        />
                                        <div
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                            %
                                        </div>
                                    </div>
                                    {hasPercentError ? (
                                        <p className="text-xs text-destructive">
                                            Введи корректный процент (0 или больше)
                                        </p>
                                    ) : (
                                        <p className="text-xs text-muted-foreground">
                                            Процент считается от остатка {Number(remaining).toFixed(2)} сом
                                        </p>
                                    )}
                                </div>

                                <div className="rounded-xl border bg-background p-3">
                                    <div className="text-xs text-muted-foreground">К оплате</div>
                                    <div className="text-base font-semibold">
                                        {withInterestTotal.toFixed(2)} сом
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Preview box */}
                    {selectedOption ? (
                        <div className="rounded-xl border bg-muted/20 p-3">
                            <div className="text-xs text-muted-foreground">Предпросмотр</div>
                            <div className="mt-2 text-sm">
                                {selectedOption === "earlyClose" ? (
                                    <>
                                        Опция: <span className="font-semibold">Досрочно</span>, сумма к оплате{" "}
                                        <span className="font-semibold">
                      {Number(remaining).toFixed(2)} сом
                    </span>
                                    </>
                                ) : selectedOption === "return" ? (
                                    <>
                                        Опция: <span className="font-semibold">Возврат</span>, сумма к оплате{" "}
                                        <span className="font-semibold">
                      {Number(initialAmount).toFixed(2)} сом
                    </span>
                                    </>
                                ) : (
                                    <>
                                        Опция: <span className="font-semibold">С процентом</span>, сумма к оплате{" "}
                                        <span className="font-semibold">
                      {Number(withInterestTotal).toFixed(2)} сом
                    </span>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-xl border bg-muted/20 p-3 text-sm text-muted-foreground">
                            Выбери опцию, чтобы увидеть итоговую сумму
                        </div>
                    )}
                </div>

                <Separator/>

                {/* Footer */}
                <div className="p-4 sm:p-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        className="sm:min-w-[120px]"
                    >
                        Отмена
                    </Button>
                    <Button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!selectedOption || hasPercentError}
                        className="sm:min-w-[160px]"
                    >
                        Подтвердить
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default EarlyClosePopup;
